import { useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import logoExpress from "@/assets/logo-express.webp";
import { useSiteLogo } from "@/hooks/useSiteContact";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Services", href: "/services" },
  { label: "Transfers", href: "/transfers" },
  { label: "Fleet", href: "/fleet" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

interface NavbarProps {
  variant?: "overlay" | "solid";
}

const Navbar = ({ variant = "overlay" }: NavbarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const siteLogo = useSiteLogo();

  return (
    <nav
      className={
        variant === "solid"
          ? "sticky top-0 left-0 right-0 z-50 bg-hero-bg/95 backdrop-blur border-b border-hero-foreground/10"
          : "absolute top-0 left-0 right-0 z-50"
      }
    >
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img src={siteLogo || logoExpress} alt="Express Punta Cana" className="h-11 md:h-14 w-auto" width={228} height={56} fetchPriority="high" decoding="async" />
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`text-hero-foreground/80 hover:text-orange-light transition-colors text-sm font-medium tracking-wide uppercase ${
                location.pathname === link.href ? "text-orange-light" : ""
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/">
            <Button size="sm" className="gap-2 font-sans font-semibold">
              <Phone className="w-4 h-4" />
              Book Now
            </Button>
          </Link>
        </div>

        <button
          type="button"
          className="md:hidden text-hero-foreground min-h-11 min-w-11 inline-flex items-center justify-center"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
        >
          {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <Menu className="w-6 h-6" aria-hidden="true" />}
        </button>

      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden glass-dark border-b border-hero-foreground/10"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-hero-foreground/80 hover:text-orange-light transition-colors text-sm font-medium ${
                    location.pathname === link.href ? "text-orange-light" : ""
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/" onClick={() => setIsOpen(false)}>
                <Button size="sm" className="w-full gap-2 font-sans font-semibold">
                  <Phone className="w-4 h-4" />
                  Book Now
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
